import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown, Download, Loader2, Check, Globe, AlertCircle, Sparkles,
  GraduationCap, ChevronRight, Lock, Calendar, BadgePercent, RefreshCw,
  Headphones, Image as ImageIcon, CircleDashed, CheckCircle2, PlayCircle,
  Trophy, Video, Award,
} from "lucide-react";
import { apiGet, apiPost, API } from "../lib/api";
import { FADE_UP } from "../lib/motionVariants";
import { TermsCheckbox } from "../components/TermsModal";
import { getI18n } from "../lib/i18n";

// Single source of truth for bilingual copy used across the VIP zone.
// Adding a new tier-specific string? Add it to BOTH it/es so the toggle works.
const UI = {
  it: {
    eyebrow: "Academia · Medicos para Italia",
    // FREE tier
    free_title: "Academia Médicos para Italia",
    free_subtitle:
      "L'accademia premium per medici stranieri che vogliono superare l'esame del Ministero della Salute italiano.",
    free_cta_buy: "Acquista accesso · €499,90",
    free_cta_subtitle: "Pagamento sicuro Stripe · Accesso 12 mesi · PDF + audio + slide + quiz",
    free_features_title: "Cosa include",
    // Highlight bullet rendered in red+gold from the start (premium accent).
    free_feature_highlight: "Video lezioni con spiegazione in italiano e spagnolo",
    free_features: [
      "Accesso completo a tutti i moduli e le lezioni",
      "Audio AI bilingue (italiano + spagnolo)",
      "Slides e quiz per ogni lezione",
      "Manuale teorico-pratico (PDF)",
      "Premium Plus incluso per 12 mesi",
      "Aggiornamenti futuri inclusi",
    ],
    // Exam attempts block — renders in normal color, then auto-highlights
    // to red+gold after 30 s on the page to draw the eye a second time.
    free_attempts_title: "Modo Esame Reale incluso",
    free_attempts_intro: "Al completamento dell'accademia, hai diritto a:",
    free_attempts_list: [
      "2 tentativi di esame reale gratuiti",
      "Se superi l'esame, scarichi il certificato ufficiale verificato",
      "Se non lo superi, puoi sbloccare 5 tentativi aggiuntivi",
      "E poi altri 7 tentativi extra scegliendo il livello di difficoltà",
    ],
    // PREMIUM tier (Premium or Premium Plus subscriber, no academy yet)
    pp_banner_title: "Sconto del 50% riservato",
    pp_banner_desc: "Come membro Premium Plus hai diritto allo sconto esclusivo del 50% sull'Academia.",
    pp_cta_buy: "Acquista con sconto · €249,95",
    pp_old_price: "€499,90",
    // ACADEMY ACTIVE tier
    welcome_title: "Benvenuto nella Zona VIP",
    welcome_subtitle:
      "Il tuo manuale è pronto e tutti i contenuti dell'accademia sono ora sbloccati.",
    download_title: "Manuale teorico-pratico",
    download_desc:
      "PDF completo bilingue. Il link è personale e si rigenera automaticamente quando lo richiedi.",
    download_cta: "Scarica il PDF",
    download_loading: "Preparo il link…",
    download_expires: "Scade il",
    language_title: "Lingua dei contenuti",
    language_desc: "Scegli la lingua principale dell'accademia. Puoi cambiarla quando vuoi.",
    saved: "Preferenza salvata",
    chapters_title: "Programma dell'accademia",
    chapters_desc: "I tuoi moduli e lezioni dell'accademia. Clicca per iniziare o continuare lo studio.",
    catalog_empty_title: "Stiamo preparando i moduli",
    catalog_empty_desc: "I primi capitoli saranno disponibili a breve.",
    lessons_count: "lezioni",
    cta_continue: "Continua",
    cta_start: "Inizia",
    cta_completed: "Completata",
    cta_locked: "Anteprima",
    cta_resume: "Riprendi",
    cta_try_free: "Prova gratis",
    free_preview_badge: "Lezione gratuita",
    lesson_audio: "Audio",
    lesson_images: "Immagini",
    lesson_completed: "Completata",
    lesson_in_progress: "In corso",
    lesson_not_started: "Da iniziare",
    purchased_at: "Acquistato il",
    // Expiration
    expires_in: "Accesso valido per",
    expires_days: "giorni",
    expiry_warn_title: "Il tuo accesso sta per scadere",
    expiry_warn_renew: "Rinnova adesso",
    expired_title: "Il tuo accesso all'Academia è scaduto",
    expired_desc: "Il tuo progresso è stato salvato. Rinnova per riprendere da dove hai lasciato.",
    expired_cta: "Rinnova accesso",
    lifetime_badge: "Accesso a vita",
    error_generic: "Qualcosa è andato storto. Riprova.",
    sim_eyebrow: "Modo Esame Reale",
    sim_title: "Simulatore Reale dell'Esame",
    sim_desc:
      "5 moduli da 30 domande. 18/30 minimo per modulo per superare. Nessuna compensazione.",
    sim_cta: "Apri il simulatore",
    sim_badge: "Nuovo",
  },
  es: {
    eyebrow: "Academia · Medicos para Italia",
    free_title: "Academia Médicos para Italia",
    free_subtitle:
      "La academia premium para médicos extranjeros que quieren aprobar el examen del Ministero della Salute italiano.",
    free_cta_buy: "Comprar acceso · €499,90",
    free_cta_subtitle: "Pago seguro Stripe · Acceso 12 meses · PDF + audio + slides + quiz",
    free_features_title: "Qué incluye",
    free_feature_highlight: "Clases en video con explicación en italiano y español",
    free_features: [
      "Acceso completo a todos los módulos y lecciones",
      "Audio AI bilingüe (italiano + español)",
      "Slides y quiz para cada lección",
      "Manual teórico-práctico (PDF)",
      "Premium Plus incluido por 12 meses",
      "Actualizaciones futuras incluidas",
    ],
    free_attempts_title: "Modo Examen Real incluido",
    free_attempts_intro: "Al completar la academia, tenés derecho a:",
    free_attempts_list: [
      "2 intentos de examen real gratuitos",
      "Si aprobás el examen, descargás el certificado oficial verificado",
      "Si no lo aprobás, podés desbloquear 5 intentos adicionales",
      "Y luego otros 7 intentos extra eligiendo el nivel de dificultad",
    ],
    pp_banner_title: "Descuento del 50% reservado",
    pp_banner_desc: "Como miembro Premium Plus tenés derecho al descuento exclusivo del 50% en la Academia.",
    pp_cta_buy: "Comprar con descuento · €249,95",
    pp_old_price: "€499,90",
    welcome_title: "Bienvenido a la Zona VIP",
    welcome_subtitle: "Tu manual está listo y todos los contenidos de la academia están desbloqueados.",
    download_title: "Manual teórico-práctico",
    download_desc: "PDF completo bilingüe. El enlace es personal y se regenera automáticamente cuando lo solicitas.",
    download_cta: "Descargar PDF",
    download_loading: "Preparando el enlace…",
    download_expires: "Caduca el",
    language_title: "Idioma de los contenidos",
    language_desc: "Elige el idioma principal de la academia. Puedes cambiarlo cuando quieras.",
    saved: "Preferencia guardada",
    chapters_title: "Programa de la academia",
    chapters_desc: "Tus módulos y lecciones de la academia. Click para empezar o continuar tu estudio.",
    catalog_empty_title: "Estamos preparando los módulos",
    catalog_empty_desc: "Los primeros capítulos estarán disponibles muy pronto.",
    lessons_count: "lecciones",
    cta_continue: "Continuar",
    cta_start: "Empezar",
    cta_completed: "Completada",
    cta_locked: "Vista previa",
    cta_resume: "Retomar",
    cta_try_free: "Prueba gratis",
    free_preview_badge: "Lección gratuita",
    lesson_audio: "Audio",
    lesson_images: "Imágenes",
    lesson_completed: "Completada",
    lesson_in_progress: "En curso",
    lesson_not_started: "Por empezar",
    purchased_at: "Adquirido el",
    expires_in: "Acceso válido por",
    expires_days: "días",
    expiry_warn_title: "Tu acceso está por caducar",
    expiry_warn_renew: "Renovar ahora",
    expired_title: "Tu acceso a la Academia ha caducado",
    expired_desc: "Tu progreso fue guardado. Renovás para retomar desde donde dejaste.",
    expired_cta: "Renovar acceso",
    lifetime_badge: "Acceso de por vida",
    error_generic: "Algo salió mal. Inténtalo de nuevo.",
    sim_eyebrow: "Modo Examen Real",
    sim_title: "Simulador Real del Examen",
    sim_desc:
      "5 módulos de 30 preguntas. Mínimo 18/30 por módulo para aprobar. Sin compensación.",
    sim_cta: "Abrir el simulador",
    sim_badge: "Nuevo",
  },
};

const formatDate = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale === "es" ? "es-ES" : "it-IT", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
};

const COVER_GRADIENT = {
  lavender: "linear-gradient(135deg,#A78BFA 0%, #C4B5FD 100%)",
  rose:     "linear-gradient(135deg,#F472B6 0%, #FBCFE8 100%)",
  sky:      "linear-gradient(135deg,#60A5FA 0%, #BAE6FD 100%)",
  mint:     "linear-gradient(135deg,#34D399 0%, #A7F3D0 100%)",
  peach:    "linear-gradient(135deg,#FB923C 0%, #FDE68A 100%)",
  amber:    "linear-gradient(135deg,#F59E0B 0%, #FCD34D 100%)",
};

// ───────── Red+Gold highlight blocks (Academia premium accent) ─────────
// `VideoHighlightBullet` is shown immediately to call out the new video
// bilingüe feature. `AttemptsBlock` starts in neutral palette and after 30 s
// auto-highlights itself with the same red+gold gradient — second look hook
// for the page (the user said "después de 30 segundos de estar la pantalla
// encendida que tambien se haga rojo con dorado").
const RED_GOLD_BG = "linear-gradient(135deg, #6B1220 0%, #9E1B32 40%, #B8862F 100%)";
const RED_GOLD_BORDER = "linear-gradient(135deg, #C9A24B, #6B1220)";

function VideoHighlightBullet({ text }) {
  return (
    <li data-testid="vip-feature-highlight"
        className="flex items-start gap-3 rounded-xl px-3.5 py-3"
        style={{
          background: RED_GOLD_BG,
          color: "#FFF8E1",
          boxShadow: "0 10px 28px -16px rgba(107,18,32,0.6)",
        }}>
      <span className="mt-0.5 shrink-0 h-6 w-6 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,215,140,0.18)",
                     border: "1px solid rgba(255,215,140,0.6)" }}>
        <Video className="h-3.5 w-3.5" style={{ color: "#FFD78C" }} />
      </span>
      <div className="flex-1 min-w-0">
        <span className="block text-[10px] uppercase tracking-[0.2em] mb-0.5"
              style={{ color: "#FFD78C" }}>
          Nuovo · Academia
        </span>
        <span className="block font-medium leading-snug">{text}</span>
      </div>
    </li>
  );
}

function AttemptsBlock({ title, intro, items, testId = "vip-attempts-block" }) {
  // 30-second timer that flips the block from neutral to red+gold. We use
  // a flag (not unmount) so the user can also click around without losing
  // the visual emphasis — once highlighted, it stays highlighted.
  const [hot, setHot] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setHot(true), 30000);
    return () => clearTimeout(id);
  }, []);

  return (
    <div data-testid={testId}
         data-hot={hot ? "1" : "0"}
         className="rounded-2xl p-5 sm:p-6 mb-6 transition-all duration-700"
         style={hot ? {
           background: RED_GOLD_BG,
           color: "#FFF8E1",
           border: "1px solid rgba(201,162,75,0.5)",
           boxShadow: "0 18px 50px -22px rgba(107,18,32,0.55)",
         } : {
           background: "white",
           color: "#112A1E",
           border: "1px solid #EBE7DC",
           boxShadow: "0 10px 28px -22px rgba(17,42,30,0.18)",
         }}>
      <div className="flex items-center gap-2 mb-3">
        <Award className="h-5 w-5"
               style={{ color: hot ? "#FFD78C" : "#7B5CE0" }} />
        <h3 className="font-serif text-xl leading-tight"
            style={{ color: hot ? "#FFF8E1" : "#112A1E" }}>
          {title}
        </h3>
      </div>
      <p className="text-sm leading-relaxed mb-3"
         style={{ color: hot ? "#FFE9B8" : "#4A5048" }}>
        {intro}
      </p>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="mt-1 shrink-0 h-4 w-4 rounded-full flex items-center justify-center"
                  style={{
                    background: hot ? "rgba(255,215,140,0.22)" : "rgba(123,92,224,0.15)",
                    border: hot ? "1px solid rgba(255,215,140,0.55)" : "none",
                  }}>
              <Check className="h-2.5 w-2.5"
                     style={{ color: hot ? "#FFD78C" : "#7B5CE0" }} />
            </span>
            <span className="text-sm leading-snug"
                  style={{ color: hot ? "#FFF8E1" : "#112A1E" }}>
              {it}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ───────── BuyCTA — Terms-gated checkout button ─────────
// Renders a Terms-and-Conditions checkbox above the Stripe link. Clicking
// the link without ticking the checkbox is intercepted (preventDefault).
// Used by all 3 paywalls so the gating is consistent.
function BuyCTA({ href, locale, testId, children, className, style, blockedHint }) {
  const [accepted, setAccepted] = useState(false);
  const ti18n = getI18n(locale).terms;
  const handleClick = (e) => {
    if (!accepted) {
      e.preventDefault();
    }
  };
  return (
    <div className="space-y-3 w-full sm:w-auto sm:inline-block">
      <div className="max-w-md">
        <TermsCheckbox language={locale} accepted={accepted}
                       onChange={setAccepted}
                       testId={`${testId}-terms`} />
      </div>
      <a
        data-testid={testId}
        href={accepted ? (href || "#") : "#"}
        target={accepted ? "_blank" : undefined}
        rel="noopener noreferrer"
        aria-disabled={!accepted}
        onClick={handleClick}
        className={className}
        style={{ ...(style || {}),
                 opacity: accepted ? 1 : 0.55,
                 cursor: accepted ? "pointer" : "not-allowed" }}
      >
        {children}
      </a>
      {!accepted ? (
        <p data-testid={`${testId}-block-hint`}
           className="text-[12px]"
           style={{ color: "#9E2A48" }}>
          {blockedHint || ti18n.block_until_accept}
        </p>
      ) : null}
    </div>
  );
}

// ───────── PAYWALL: FREE state ─────────
function PaywallFree({ data, t, locale }) {
  return (
    <motion.section data-testid="vip-paywall-free"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-3xl mx-auto">
      <p className="text-[11px] uppercase tracking-[0.22em] text-brand-secondary mb-3">
        {t.eyebrow}
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-tight mb-4">
        {t.free_title}
      </h1>
      <p className="text-brand-secondary text-lg leading-relaxed mb-6">
        {t.free_subtitle}
      </p>

      <div className="rounded-2xl border border-brand-border bg-white shadow-premium p-6 sm:p-8 mb-6">
        <h2 className="font-serif text-2xl text-brand-primary mb-4">
          {t.free_features_title}
        </h2>
        <ul className="space-y-3" data-testid="vip-paywall-features">
          <VideoHighlightBullet text={t.free_feature_highlight} />
          {t.free_features.map((f, i) => (
            <li key={i} className="flex items-start gap-3 text-brand-primary">
              <span className="mt-1 shrink-0 h-5 w-5 rounded-full bg-brand-green/15 flex items-center justify-center">
                <Check className="h-3 w-3 text-brand-green" />
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      <AttemptsBlock
        title={t.free_attempts_title}
        intro={t.free_attempts_intro}
        items={t.free_attempts_list}
        testId="vip-paywall-attempts"
      />

      <BuyCTA
        href={data?.payment_url}
        locale={locale}
        testId="vip-paywall-buy-btn"
        className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-brand-primary text-white font-semibold shadow-premium-lg hover:bg-brand-green transition-colors"
      >
        <Crown className="h-5 w-5" /> {t.free_cta_buy}
      </BuyCTA>
      <p className="text-sm text-brand-secondary mt-3 leading-relaxed">
        {t.free_cta_subtitle}
      </p>
    </motion.section>
  );
}

// ───────── PAYWALL: Premium / Premium Plus user (50% off) ─────────
function PaywallPremiumPlus({ data, t, locale }) {
  return (
    <motion.section data-testid="vip-paywall-pp"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-3xl mx-auto">
      <p className="text-[11px] uppercase tracking-[0.22em] text-brand-secondary mb-3">
        {t.eyebrow}
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-tight mb-4">
        {t.free_title}
      </h1>

      {/* Discount banner — high-impact, gold-accented */}
      <div data-testid="vip-pp-discount-banner"
           className="rounded-2xl p-6 mb-6 shadow-premium-lg flex items-start gap-4 border"
           style={{
             background: "linear-gradient(135deg, #FFF8E7 0%, #FDE7F1 100%)",
             borderColor: "#E0C880",
           }}>
        <div className="shrink-0 h-12 w-12 rounded-full grid place-items-center"
             style={{ background: "rgba(217,119,6,0.15)" }}>
          <BadgePercent className="h-6 w-6" style={{ color: "#B45309" }} />
        </div>
        <div className="flex-1">
          <p className="font-serif text-xl mb-1" style={{ color: "#7A4F00" }}>
            {t.pp_banner_title}
          </p>
          <p className="text-sm leading-relaxed" style={{ color: "#7A4F00" }}>
            {t.pp_banner_desc}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-brand-border bg-white shadow-premium p-6 sm:p-8 mb-6">
        <ul className="space-y-3 mb-5" data-testid="vip-pp-features">
          <VideoHighlightBullet text={t.free_feature_highlight} />
          {t.free_features.map((f, i) => (
            <li key={i} className="flex items-start gap-3 text-brand-primary">
              <span className="mt-1 shrink-0 h-5 w-5 rounded-full bg-brand-green/15 flex items-center justify-center">
                <Check className="h-3 w-3 text-brand-green" />
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="pt-4 border-t border-brand-border flex flex-wrap items-baseline gap-3">
          <span className="text-3xl font-serif font-bold" style={{ color: "#7B5CE0" }}>
            €{(data.price_final_eur || 249.95).toString().replace(".", ",")}
          </span>
          <span className="text-lg line-through" style={{ color: "#9aa19c" }}>
            {t.pp_old_price}
          </span>
          <span className="text-xs uppercase tracking-[0.18em] px-2 py-1 rounded-full font-semibold"
                style={{ background: "#FCEAEE", color: "#9E2A48" }}>
            -50%
          </span>
        </div>
      </div>

      <AttemptsBlock
        title={t.free_attempts_title}
        intro={t.free_attempts_intro}
        items={t.free_attempts_list}
        testId="vip-pp-attempts"
      />

      <BuyCTA
        href={data?.payment_url}
        locale={locale}
        testId="vip-pp-buy-btn"
        className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-white font-semibold transition-colors"
        style={{ background: "#7B5CE0" }}
      >
        <Crown className="h-5 w-5" /> {t.pp_cta_buy}
      </BuyCTA>
    </motion.section>
  );
}

// ───────── EXPIRED state ─────────
function PaywallExpired({ data, t, locale }) {
  return (
    <motion.section data-testid="vip-paywall-expired"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-3xl mx-auto">
      <div className="rounded-2xl border-2 p-7 mb-6"
           style={{ borderColor: "#E25C7E", background: "#FFF5F8" }}>
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-12 w-12 rounded-full grid place-items-center"
               style={{ background: "#FCEAEE" }}>
            <AlertCircle className="h-6 w-6" style={{ color: "#9E2A48" }} />
          </div>
          <div>
            <h2 className="font-serif text-2xl mb-2" style={{ color: "#9E2A48" }}>
              {t.expired_title}
            </h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "#7A2A48" }}>
              {t.expired_desc}
            </p>
            <BuyCTA
              href={data?.payment_url}
              locale={locale}
              testId="vip-renew-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold"
              style={{ background: "#7B5CE0" }}
            >
              <RefreshCw className="h-4 w-4" /> {t.expired_cta}
            </BuyCTA>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// ───────── ACTIVE state (unchanged from before, with expiry banner) ─────────
function VipDashboard({ data, t, locale, onLanguageChange, savedFlash, onLogout }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState(data?.download || null);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const fresh = await apiGet("/vip/me");
      setDownloadInfo(fresh.download || null);
      if (fresh.download?.url_path) {
        window.open(`${API}${fresh.download.url_path.replace(/^\/api/, "")}`, "_blank", "noopener,noreferrer");
      }
    } catch {
      if (downloadInfo?.url_path) {
        window.open(`${API}${downloadInfo.url_path.replace(/^\/api/, "")}`, "_blank", "noopener,noreferrer");
      }
    } finally { setDownloading(false); }
  };

  const purchasedDate = useMemo(
    () => formatDate(data?.vip_purchased_at, locale),
    [data?.vip_purchased_at, locale]
  );
  const expiresDate = useMemo(
    () => formatDate(downloadInfo?.expires_at, locale),
    [downloadInfo?.expires_at, locale]
  );
  const showExpiryWarning = !data.academy_lifetime
    && data.days_until_expiry !== undefined
    && data.days_until_expiry <= 30;

  return (
    <motion.section data-testid="vip-dashboard"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-4xl mx-auto">
      <p className="text-[11px] uppercase tracking-[0.22em] text-brand-secondary mb-3 inline-flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> {t.eyebrow}
      </p>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-tight">
          {t.welcome_title}
        </h1>
        {data.academy_lifetime ? (
          <span data-testid="vip-lifetime-badge"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: "#E8F5EE", color: "#1A6B3A" }}>
            <Crown className="h-3.5 w-3.5" /> {t.lifetime_badge}
          </span>
        ) : data.days_until_expiry !== undefined ? (
          <span data-testid="vip-days-left"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: "#EDE7FF", color: "#5A3FC0" }}>
            <Calendar className="h-3.5 w-3.5" /> {t.expires_in} <strong>{data.days_until_expiry}</strong> {t.expires_days}
          </span>
        ) : null}
      </div>
      <p className="text-brand-secondary text-lg leading-relaxed mb-2">
        {t.welcome_subtitle}
      </p>
      {purchasedDate ? (
        <p className="text-sm text-brand-secondary mb-6" data-testid="vip-purchased-date">
          {t.purchased_at} <strong className="text-brand-primary">{purchasedDate}</strong>
        </p>
      ) : null}

      {showExpiryWarning ? (
        <div data-testid="vip-expiry-warning"
             className="rounded-2xl p-4 mb-6 flex items-center gap-3 flex-wrap"
             style={{ background: "#FFF8E7", border: "1px solid #E0C880" }}>
          <AlertCircle className="h-5 w-5" style={{ color: "#B45309" }} />
          <p className="flex-1 text-sm" style={{ color: "#7A4F00" }}>
            <strong>{t.expiry_warn_title}</strong>
          </p>
          <a href={data?.payment_url || "#"} target="_blank" rel="noopener noreferrer"
             data-testid="vip-expiry-renew"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
             style={{ background: "#7B5CE0" }}>
            <RefreshCw className="h-3.5 w-3.5" /> {t.expiry_warn_renew}
          </a>
        </div>
      ) : null}

      <div data-testid="vip-download-card"
           className="rounded-2xl border border-brand-green/30 bg-gradient-to-br from-brand-green/5 to-white shadow-premium p-6 sm:p-8 mb-6">
        <h2 className="font-serif text-2xl text-brand-primary mb-2 flex items-center gap-2">
          <Download className="h-5 w-5 text-brand-green" /> {t.download_title}
        </h2>
        <p className="text-brand-secondary mb-5 leading-relaxed">{t.download_desc}</p>
        <button data-testid="vip-download-btn" onClick={handleDownload} disabled={downloading}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-primary text-white font-medium hover:opacity-90 disabled:opacity-50">
          {downloading
            ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.download_loading}</>
            : <><Download className="h-4 w-4" /> {t.download_cta}</>}
        </button>
        {expiresDate ? (
          <p className="text-xs text-brand-secondary mt-3" data-testid="vip-download-expires">
            {t.download_expires} <strong>{expiresDate}</strong>
          </p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-brand-border bg-white shadow-premium p-6 sm:p-8 mb-6">
        <h2 className="font-serif text-2xl text-brand-primary mb-2 flex items-center gap-2">
          <Globe className="h-5 w-5 text-brand-secondary" /> {t.language_title}
        </h2>
        <p className="text-brand-secondary mb-3 leading-relaxed">{t.language_desc}</p>
        <LanguageToggle current={locale} onChange={onLanguageChange} />
        {savedFlash ? (
          <p className="text-sm text-brand-green mt-3 inline-flex items-center gap-1.5"
             data-testid="vip-language-saved">
            <Check className="h-4 w-4" /> {t.saved}
          </p>
        ) : null}
      </div>

      <ModulesCatalog t={t} hasAccess={data.has_academy_access} />
      <SimulatorCta t={t} locale={locale} />
    </motion.section>
  );
}

// ───────── Simulator (Modo Examen Real) entry CTA ─────────
function SimulatorCta({ t, locale }) {
  return (
    <Link to="/vip/exam" data-testid="vip-simulator-cta"
          className="block rounded-2xl mb-6 overflow-hidden shadow-premium-lg hover:shadow-premium-lg transition-shadow"
          style={{
            background: "linear-gradient(135deg, #2C1862 0%, #5A3FC0 60%, #B45309 100%)",
          }}>
      <div className="p-6 sm:p-8 flex items-center gap-5 flex-wrap">
        <div className="h-14 w-14 rounded-2xl grid place-items-center shrink-0"
             style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}>
          <Trophy className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0 text-white">
          <p className="text-[10px] uppercase tracking-[0.22em] opacity-90 mb-1">
            {t.sim_eyebrow} <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                  style={{ background: "#FFE08A", color: "#5A3FC0" }}>
              {t.sim_badge}
            </span>
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl leading-tight">{t.sim_title}</h3>
          <p className="text-sm sm:text-base opacity-90 leading-relaxed mt-1">
            {t.sim_desc}
          </p>
        </div>
        <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-brand-primary font-semibold whitespace-nowrap"
              data-testid="vip-simulator-cta-btn">
          {t.sim_cta} <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

function LanguageToggle({ current, onChange, disabled }) {
  return (
    <div data-testid="vip-language-toggle" className="flex items-center gap-2 flex-wrap">
      {[{ code: "it", label: "Italiano" }, { code: "es", label: "Español" }].map((opt) => (
        <button key={opt.code} data-testid={`vip-lang-${opt.code}`}
                onClick={() => !disabled && onChange(opt.code)} disabled={disabled}
                className={[
                  "px-4 py-2 rounded-full text-sm font-medium border transition-colors",
                  current === opt.code
                    ? "bg-brand-primary text-white border-brand-primary"
                    : "bg-white text-brand-primary border-brand-border hover:border-brand-primary",
                  disabled ? "opacity-60 cursor-not-allowed" : "",
                ].join(" ")}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ───────── Module catalog (also used by paywall) ─────────
function ModuleCard({ mod, t, hasAccess }) {
  const grad = COVER_GRADIENT[mod.cover] || COVER_GRADIENT.lavender;
  const pct = mod.progress_pct || 0;
  const lessons = mod.lessons || [];
  const hasLessons = lessons.length > 0;
  const freePreviewCount = lessons.filter((l) => l.is_free_preview).length;
  // Resume target: first non-completed lesson the user can actually open.
  const resumeLesson = (() => {
    const candidates = hasAccess ? lessons : lessons.filter((l) => l.is_free_preview);
    return candidates.find((l) => !l.completed) || candidates[0];
  })();
  const headerCta = !hasAccess
    ? (resumeLesson ? t.cta_try_free : t.cta_locked)
    : pct === 100 ? t.cta_completed
    : pct > 0 ? t.cta_resume
    : t.cta_start;
  const resumeHint = resumeLesson ? resumeLesson.title : "";
  return (
    <motion.div data-testid={`vip-module-card-${mod.slug}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl overflow-hidden shadow-premium border border-brand-border bg-white relative">
      {!hasAccess && freePreviewCount === 0 ? (
        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium inline-flex items-center gap-1"
             style={{ background: "rgba(255,255,255,0.95)", color: "#9E2A48" }}>
          <Lock className="h-3 w-3" /> Locked
        </div>
      ) : null}
      <div className="h-32 relative" style={{ background: grad, opacity: hasAccess ? 1 : 0.7 }}>
        {mod.cover_image_url ? (
          <img src={mod.cover_image_url} alt={mod.title}
               className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <GraduationCap className="h-12 w-12 text-white/85" strokeWidth={1.6} />
          </div>
        )}
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-medium"
             style={{ background: "rgba(255,255,255,0.85)", color: "#5A3FC0" }}>
          {mod.lesson_count} {t.lessons_count}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-serif text-xl text-brand-primary mb-1 leading-tight">
          {mod.title}
        </h3>
        {mod.description ? (
          <p className="text-sm text-brand-secondary leading-relaxed mb-4 line-clamp-2">
            {mod.description}
          </p>
        ) : null}
        {hasAccess ? (
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-1.5 rounded-full bg-brand-border/60 overflow-hidden">
              <div className="h-full rounded-full transition-[width] duration-500"
                   style={{ width: `${pct}%`, background: "#7B5CE0" }} />
            </div>
            <span className="text-xs font-medium tabular-nums" style={{ color: "#5A3FC0" }}>{pct}%</span>
          </div>
        ) : null}

        {/* Resume / start CTA — jumps to next-incomplete lesson the user can open */}
        {hasLessons && resumeLesson ? (
          <Link to={`/vip/lessons/${resumeLesson.lesson_id}`}
                data-testid={`vip-module-cta-${mod.slug}`}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-white transition-all hover:opacity-90 mb-4 text-left"
                style={{ background: "#7B5CE0" }}>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-85 font-medium">
                {headerCta}
              </p>
              {resumeHint ? (
                <p className="text-sm font-medium leading-snug line-clamp-1">
                  {resumeHint}
                </p>
              ) : null}
            </div>
            <ChevronRight className="h-5 w-5 shrink-0" />
          </Link>
        ) : null}

        {/* Per-lesson list — visible to ALL users (FREE included). Locked
            rows are still listed but click takes user to teaser/upgrade. */}
        {hasLessons ? (
          <ul className="space-y-1.5" data-testid={`vip-module-lesson-list-${mod.slug}`}>
            {lessons.map((l, i) => (
              <LessonListItem key={l.lesson_id} l={l} idx={i} t={t}
                              hasAccess={hasAccess} />
            ))}
          </ul>
        ) : null}
      </div>
    </motion.div>
  );
}

// One row in the per-module lesson list
function LessonListItem({ l, idx, t, hasAccess }) {
  const accessible = hasAccess || l.is_free_preview;
  // Free-preview rows get a flashy red+gold "Academia premium" treatment so
  // they pop in the catalog and pull free users into clicking. Reuses the
  // same vip-shimmer / vip-pulse / vip-sweep keyframes already loaded in
  // App.css so we don't have to ship new CSS.
  const showcase = l.is_free_preview && !hasAccess;
  if (showcase) {
    return (
      <Link to={`/vip/lessons/${l.lesson_id}`}
            data-testid={`vip-lesson-item-${l.lesson_id}`}
            data-free-showcase="1"
            className="group relative flex items-start gap-3 px-3.5 py-3 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.015]"
            style={{
              background: "linear-gradient(135deg, #B91C1C 0%, #DC2626 25%, #EAB308 50%, #DC2626 75%, #B91C1C 100%)",
              backgroundSize: "300% 300%",
              animation: "vip-shimmer 4.5s ease-in-out infinite",
              color: "#FFF8E1",
              border: "1px solid rgba(255, 220, 130, 0.4)",
              boxShadow: "0 6px 18px -6px rgba(220,38,38,0.4)",
            }}>
        {/* Pulsing outer glow */}
        <span aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-xl"
              style={{
                boxShadow: "0 0 16px 2px rgba(234,179,8,0.45)",
                animation: "vip-pulse 2.6s ease-in-out infinite",
              }} />
        {/* Diagonal sweep — visible always, brighter on hover */}
        <span aria-hidden="true"
              className="pointer-events-none absolute -inset-1 rounded-xl opacity-60 group-hover:opacity-100 transition-opacity"
              style={{
                background: "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)",
                backgroundSize: "200% 100%",
                animation: "vip-sweep 3.2s linear infinite",
                mixBlendMode: "overlay",
              }} />
        {/* Crown icon "ribbon" */}
        <span className="relative z-10 inline-flex items-center justify-center h-7 w-7 rounded-lg shrink-0 self-center"
              style={{ background: "rgba(255,248,225,0.18)",
                       border: "1px solid rgba(255,248,225,0.35)" }}>
          <Sparkles className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12"
                    style={{ color: "#FFD78C",
                             filter: "drop-shadow(0 0 4px rgba(255,255,255,0.55))" }} />
        </span>
        <div className="flex-1 min-w-0 relative z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-1.5 py-0.5 rounded"
                  style={{ background: "rgba(255,248,225,0.18)",
                           border: "1px solid rgba(255,215,140,0.4)",
                           color: "#FFD78C" }}
                  data-testid="vip-free-preview-badge">
              {t.free_preview_badge}
            </span>
            <span className="text-[10px] font-mono tabular-nums tracking-wider opacity-75">
              {String(idx + 1).padStart(2, "0")}
            </span>
          </div>
          <p className="text-sm font-medium leading-snug line-clamp-2 mt-1"
             style={{ textShadow: "0 1px 2px rgba(0,0,0,0.18)" }}>
            {l.title}
          </p>
          <p className="text-[11px] mt-1 opacity-85 flex items-center gap-1">
            <PlayCircle className="h-3 w-3" /> {t.cta_try_free}
          </p>
        </div>
        <ChevronRight className="relative z-10 h-4 w-4 self-center transition-transform duration-300 group-hover:translate-x-1"
                      style={{ color: "#FFD78C",
                               filter: "drop-shadow(0 0 4px rgba(255,255,255,0.5))" }} />
      </Link>
    );
  }
  let StatusIcon;
  let statusColor;
  if (!accessible) {
    StatusIcon = Lock; statusColor = "#C0BCD9";
  } else if (l.completed) {
    StatusIcon = CheckCircle2; statusColor = "#16A34A";
  } else if (idx === 0) {
    StatusIcon = PlayCircle; statusColor = "#7B5CE0";
  } else {
    StatusIcon = CircleDashed; statusColor = "#7B5CE0";
  }
  return (
    <Link to={`/vip/lessons/${l.lesson_id}`}
          data-testid={`vip-lesson-item-${l.lesson_id}`}
          className={`group flex items-start gap-3 px-3 py-2.5 rounded-xl border transition-all ${accessible ? "border-brand-border/60 hover:border-brand-primary hover:bg-brand-primary/5" : "border-brand-border/40 opacity-75 hover:opacity-100"}`}>
      <StatusIcon className="h-5 w-5 shrink-0 mt-0.5" style={{ color: statusColor }} strokeWidth={1.8} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-mono tabular-nums tracking-wider"
                style={{ color: "#9E97BA" }}>
            {String(idx + 1).padStart(2, "0")}
          </span>
          <p className="text-sm leading-snug line-clamp-2 text-brand-primary group-hover:text-brand-primary">
            {l.title}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {l.is_free_preview ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
                  style={{ color: "#15803D", background: "#DCFCE7" }}
                  data-testid="vip-free-preview-badge">
              <Sparkles className="h-3 w-3" /> {t.free_preview_badge}
            </span>
          ) : null}
          {l.completed ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
                  style={{ color: "#16A34A", background: "#DCFCE7" }}>
              {t.lesson_completed}
            </span>
          ) : null}
          {accessible && l.audio_status === "ready" ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-brand-secondary">
              <Headphones className="h-3 w-3" /> {t.lesson_audio}
            </span>
          ) : null}
          {accessible && l.image_count > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-brand-secondary">
              <ImageIcon className="h-3 w-3" /> {l.image_count} {t.lesson_images.toLowerCase()}
            </span>
          ) : null}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 self-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: "#7B5CE0" }} />
    </Link>
  );
}

function ModulesCatalog({ t, hasAccess }) {
  const [modules, setModules] = useState(null);
  useEffect(() => {
    apiGet("/vip/modules").then((r) => setModules(r.modules || [])).catch(() => setModules([]));
  }, []);
  return (
    <div data-testid="vip-catalog" className="mt-2">
      <h2 className="font-serif text-2xl text-brand-primary mb-2">{t.chapters_title}</h2>
      <p className="text-brand-secondary mb-6 leading-relaxed">{t.chapters_desc}</p>
      {modules === null
        ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 text-brand-secondary animate-spin mx-auto" /></div>
        : modules.length === 0
          ? <div data-testid="vip-catalog-empty" className="rounded-2xl border border-brand-border bg-white p-8 text-center">
              <GraduationCap className="h-9 w-9 text-brand-secondary/50 mx-auto mb-3" />
              <p className="font-serif text-lg text-brand-primary mb-1">{t.catalog_empty_title}</p>
              <p className="text-sm text-brand-secondary max-w-md mx-auto">{t.catalog_empty_desc}</p>
            </div>
          : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {modules.map((m) => <ModuleCard key={m.module_id} mod={m} t={t} hasAccess={hasAccess} />)}
            </div>}
    </div>
  );
}

// ───────── ROOT ─────────
export default function VIP() {
  const [data, setData] = useState(null);
  const [locale, setLocale] = useState("it");
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    let active = true;
    apiGet("/vip/me").then((d) => {
      if (!active) return;
      setData(d);
      if (d?.preferred_language) setLocale(d.preferred_language);
    }).catch(() => active && setError("__GENERIC__"));
    return () => { active = false; };
  }, []);

  const handleLanguageChange = async (next) => {
    if (!data?.has_academy_access || next === locale) return;
    const prev = locale;
    setLocale(next);
    try {
      await apiPost("/vip/language", { language: next });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch { setLocale(prev); }
  };

  const t = UI[locale] || UI.it;
  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="vip-error">
        <AlertCircle className="h-8 w-8 text-brand-red mx-auto mb-3" />
        <p className="text-brand-primary">{t.error_generic}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="vip-loading">
        <Loader2 className="h-6 w-6 text-brand-secondary animate-spin mx-auto" />
      </div>
    );
  }

  // Tier dispatch
  const tier = data.tier;
  // Detect "expired" — was active, now isn't
  const isExpired = (
    !data.has_academy_access
    && data.vip_purchased_at  // bought at some point
    && data.academy_renewals_count > 0  // has used the academy
  );

  return (
    <div data-testid="vip-page" className="px-4 sm:px-8 py-10">
      {tier === "academy"
        ? <VipDashboard data={data} t={t} locale={locale}
                        onLanguageChange={handleLanguageChange} savedFlash={savedFlash} />
        : isExpired
          ? <PaywallExpired data={data} t={t} locale={locale} />
          : tier === "premium_plus"
            ? <PaywallPremiumPlus data={data} t={t} locale={locale} />
            : <PaywallFree data={data} t={t} locale={locale} />}

      {/* Always show locked catalog under the paywall so users see what's behind it */}
      {tier !== "academy" && !isExpired ? (
        <div className="max-w-4xl mx-auto mt-12">
          <ModulesCatalog t={t} hasAccess={false} />
        </div>
      ) : null}
    </div>
  );
}
